// Canned rotation templates. Each generates a full 2-week rotation config
// that matches Divii's existing Parenting Time Schedule patterns.
//
// Convention:
//   - Sunday-anchored week (sun=0, mon=1, …, sat=6).
//   - Events use lowercase 3-letter day names.
//   - Times are 'HH:MM' 24-hour.
//   - An event whose endDay×endTime precedes startDay×startTime wraps forward.
//   - `transition: 'A_drops' | 'B_drops' | 'A_picks' | 'B_picks' | null`
//     — pre-populated with `<partner>_drops` since the ending partner
//     typically hands off. Users can flip via the edit modal.
//
// All templates use 2pm (14:00) as the standard handoff time, matching
// Divii's convention. Week On/Off uses 3pm (15:00) on Fridays specifically.

import { DAY_INDEX, MIN_PER_DAY, timeToMin } from './weekMath.js'

// Builds a single event. Fifth positional param is optional options; supply
// { endWeekOffset: 1 } for events that wrap into the following week (e.g. a
// Fri → Mon weekend). If omitted, endWeekOffset is auto-inferred from day
// ordering: end-before-start in the same week → wrap; else same week.
function ev(partner, startDay, startTime, endDay, endTime, opts = {}) {
  const startPos = (DAY_INDEX[startDay] ?? 0) * MIN_PER_DAY + timeToMin(startTime)
  const endPos = (DAY_INDEX[endDay] ?? 0) * MIN_PER_DAY + timeToMin(endTime)
  const endWeekOffset = opts.endWeekOffset !== undefined
    ? opts.endWeekOffset
    : (endPos <= startPos ? 1 : 0)
  return {
    partner,
    startDay,
    startTime,
    endDay,
    endTime,
    startWeekOffset: 0,
    endWeekOffset,
    transition: `${partner}_drops`,   // ending partner drops off by default
    transitionDetails: '',
  }
}

export const TEMPLATES = [
  {
    key: 'week-on-off',
    title: 'Week On / Off',
    description: 'One parent has full custody for a week, then the other. Friday 3pm handoffs.',
    apply: () => ({
      rotationLength: 2,
      weeks: [
        // Week 1: A owns Fri 3pm → Fri 3pm (wraps into Week 2)
        { events: [ ev('A', 'fri', '15:00', 'fri', '15:00') ] },
        // Week 2: B owns Fri 3pm → Fri 3pm (wraps to next cycle's Fri)
        { events: [ ev('B', 'fri', '15:00', 'fri', '15:00') ] },
      ],
    }),
  },
  {
    key: '2-2-3',
    title: '2 / 2 / 3',
    description: 'Every week: 2 days A, 2 days B, 3 days the third block. Blocks alternate weekly so both parents get the "3".',
    apply: () => ({
      rotationLength: 2,
      weeks: [
        { events: [
          ev('A', 'mon', '14:00', 'wed', '14:00'),   // 2 days
          ev('B', 'wed', '14:00', 'fri', '14:00'),   // 2 days
          ev('A', 'fri', '14:00', 'mon', '14:00'),   // 3 days (wraps into Week 2 Mon)
        ]},
        { events: [
          ev('B', 'mon', '14:00', 'wed', '14:00'),
          ev('A', 'wed', '14:00', 'fri', '14:00'),
          ev('B', 'fri', '14:00', 'mon', '14:00'),   // 3 days (wraps to next cycle)
        ]},
      ],
    }),
  },
  {
    key: 'every-other-weekend',
    title: 'Every Other Weekend',
    description: 'One parent has weekdays every week; the other has every other weekend (Fri 3pm → Sun 6pm).',
    apply: () => ({
      rotationLength: 2,
      // A's time is one continuous block: Sun 6pm of Week 1 round to Fri 3pm
      // of the NEXT cycle's Week 1 (11 days 21 hours), so it ends two week
      // boundaries after it starts (endWeekOffset 2). Week 2 holds no block
      // of its own: A's block covers all of it.
      weeks: [
        { events: [
          ev('B', 'fri', '15:00', 'sun', '18:00'),                       // B every-other weekend (Fri 3pm → Sun 6pm, W1)
          ev('A', 'sun', '18:00', 'fri', '15:00', { endWeekOffset: 2 }),  // A Sun 6pm W1 → Fri 3pm W1 of the next cycle
        ]},
        { events: [] },
      ],
    }),
  },
  {
    key: '2-2-5-5',
    title: '2 / 2 / 5 / 5',
    description: 'A keeps Mon-Tue, B keeps Wed-Thu, and the two 5-day blocks (weekend + weekdays) alternate weekly.',
    apply: () => ({
      rotationLength: 2,
      weeks: [
        { events: [
          ev('A', 'mon', '14:00', 'wed', '14:00'),
          ev('B', 'wed', '14:00', 'fri', '14:00'),
          ev('A', 'fri', '14:00', 'wed', '14:00'),   // Fri W1 → Tue W2 (5 days, wraps)
        ]},
        { events: [
          ev('B', 'wed', '14:00', 'mon', '14:00'),   // Wed W2 → Sun W2 (5 days, wraps to next cycle Mon)
        ]},
      ],
    }),
  },
  {
    key: '3-4-3-4',
    title: '3 / 4 / 3 / 4',
    description: 'A has Wed-Fri (3), B has Sat-Tue (4). Weekly pattern; second week matches first.',
    apply: () => ({
      rotationLength: 2,
      weeks: [
        { events: [
          ev('A', 'wed', '14:00', 'sat', '14:00'),   // 3 days: Wed, Thu, Fri
          ev('B', 'sat', '14:00', 'wed', '14:00'),   // 4 days: Sat, Sun of Wk1 + Mon, Tue of Wk2 (wraps)
        ]},
        { events: [
          ev('A', 'wed', '14:00', 'sat', '14:00'),
          ev('B', 'sat', '14:00', 'wed', '14:00'),   // wraps to next cycle Wed
        ]},
      ],
    }),
  },
  {
    key: '3-3-4-4',
    title: '3 / 3 / 4 / 4',
    description: 'Week 1 has two 3-day blocks; Week 2 has two 4-day blocks. Balanced 50/50 across the cycle.',
    apply: () => ({
      rotationLength: 2,
      weeks: [
        { events: [
          ev('A', 'wed', '14:00', 'sat', '14:00'),   // 3 days: Wed, Thu, Fri
          ev('B', 'sat', '14:00', 'tue', '14:00'),   // 3 days: Sat W1, Sun W1, Mon W2 (wraps)
        ]},
        { events: [
          ev('A', 'tue', '14:00', 'sat', '14:00'),   // 4 days: Tue, Wed, Thu, Fri of W2
          ev('B', 'sat', '14:00', 'wed', '14:00'),   // 4 days: Sat W2, Sun W2, Mon-Tue of next cycle (wraps)
        ]},
      ],
    }),
  },
  {
    key: 'every-weekend',
    title: 'Every Weekend',
    description: 'One parent has weekdays every week; the other has every weekend (Fri 2pm → Mon 2pm).',
    apply: () => ({
      rotationLength: 2,
      weeks: [
        { events: [
          ev('A', 'mon', '14:00', 'fri', '14:00'),   // A weekdays
          ev('B', 'fri', '14:00', 'mon', '14:00'),   // B weekend (wraps into Week 2 Mon)
        ]},
        { events: [
          ev('A', 'mon', '14:00', 'fri', '14:00'),   // Same pattern Week 2
          ev('B', 'fri', '14:00', 'mon', '14:00'),   // wraps to next cycle Mon
        ]},
      ],
    }),
  },
]

// Blank week factory used by the + Add Week control.
export function blankWeek() {
  return { events: [] }
}

// Blank rotation returned when there's no saved state and no template is applied.
export function blankRotation(anchorDate) {
  return {
    rotationLength: 2,
    anchorDate: anchorDate || nextMondayISO(new Date()),
    weeks: [blankWeek(), blankWeek()],
  }
}

function nextMondayISO(from) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const dow = d.getDay()  // 0=Sun … 6=Sat
  const daysToMon = (1 - dow + 7) % 7 || 7  // if today is Mon, go to next Mon
  d.setDate(d.getDate() + daysToMon)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
